import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import {
  getDashboardWidgetStorageKey,
  getLegacyDashboardWidgetStorageKey,
  loadLocalDashboardWidgets,
  normalizeDashboardWidgets,
  saveLocalDashboardWidgets,
} from "./localWidgets";
import {
  getSharedDashboardLayout,
  saveSharedDashboardWidgets,
} from "@/features/machines/layoutApi";
import type { DashboardWidget } from "./types";
import { confirmLayoutResponse, type LayoutConfirmation } from "@/features/machines/layoutConfirmation";
import { jsonStructurallyEqual } from "@/lib/jsonEquality";

interface UseDashboardWidgetsParams {
  enabled?: boolean;
  workspaceId: string;
  machineId: string;
  /**
   * Versao da configuracao fisica que originou os widgets automaticos.
   * Quando o gateway publica um novo meta/configHash, o layout salvo antigo
   * nao deve reaparecer como sinal fantasma em cima da nova configuracao.
   */
  configVersion?: string | null;
  /** Escopo do operador atual. Enquanto nao ha login real, usa `operator-local`. */
  userId?: string | null;
  /** Fontes fisicas declaradas no meta atual; usado apenas para filtrar migracao sem versao. */
  validSources?: Iterable<string> | null;
}

export interface UseDashboardWidgetsResult {
  /** GET confirmado no escopo atual; cache/preview e escrita local nao sao confirmacao. */
  remoteConfirmation: LayoutConfirmation | null;
  /** Widgets persistidos pelo operador. Vazio quando ainda há só a prévia automática. */
  savedWidgets: DashboardWidget[];
  /** Adiciona ao layout; `seedWidgets` promove a previa automatica no primeiro save. */
  addWidget: (widget: DashboardWidget, seedWidgets?: DashboardWidget[]) => void;
  /** Remove um widget pelo id. */
  removeWidget: (id: string) => void;
  /** Atualiza exatamente um widget por id, preservando cards da mesma source. */
  upsertWidget: (widget: DashboardWidget, seedWidgets?: DashboardWidget[]) => void;
  /** Lote: uma gravacao so para N cards. Ver o comentario na implementacao. */
  upsertWidgets: (widgets: DashboardWidget[], seedWidgets?: DashboardWidget[]) => void;
  /** Regrava a lista com `layout.order` sequencial após reordenação visual. */
  reorderWidgets: (widgets: DashboardWidget[]) => void;
  /** Apaga toda a lista — volta para a prévia automática. */
  reset: () => void;
}

/**
 * Hook único de gerenciamento da lista persistida de widgets do dashboard.
 *
 * Centralizar aqui evita que dois componentes (página + painel) leiam o
 * `localStorage` independentemente e desincronizem. Quem precisa renderizar
 * recebe `savedWidgets`; quem precisa mutar usa `addWidget`/`removeWidget`/
 * `upsertWidget`/`reset`.
 */
export function useDashboardWidgets({
  enabled = true,
  workspaceId,
  machineId,
  configVersion,
  userId,
  validSources,
}: UseDashboardWidgetsParams): UseDashboardWidgetsResult {
  const storageKey = useMemo(
    () => getDashboardWidgetStorageKey(workspaceId, machineId, configVersion, userId),
    [workspaceId, machineId, configVersion, userId],
  );
  const legacyStorageKey = useMemo(
    () => getLegacyDashboardWidgetStorageKey(workspaceId, machineId, configVersion),
    [workspaceId, machineId, configVersion],
  );
  const legacyUnversionedStorageKey = useMemo(
    () => getLegacyDashboardWidgetStorageKey(workspaceId, machineId),
    [workspaceId, machineId],
  );
  // Chave CANÔNICA (string) das fontes válidas — nunca o Set/array cru nos
  // deps do efeito. Com um caller passando array de identidade instável
  // (inline/recriado por render), um dep de objeto novo a cada render fazia
  // efeito → setState → render → efeito... loop infinito (OOM real na suíte).
  // String igual = dep igual: o efeito só re-roda quando o CONTEÚDO muda.
  const validSourcesKey = useMemo(() => {
    if (!validSources) return "";
    const values = [...validSources]
      .map((source) => source.trim())
      .filter((source) => source.length > 0);
    return [...new Set(values)].sort().join("|");
  }, [validSources]);
  const [savedWidgets, setSavedWidgets] = useState<DashboardWidget[]>([]);
  const confirmationScope = `${storageKey}:${validSourcesKey}`;
  const [confirmation, setConfirmation] = useState<{
    scope: string; value: LayoutConfirmation | null;
  } | null>(null);
  const localRevision = useRef(0);

  useEffect(() => {
    if (typeof window === "undefined" || !enabled) return;
    const controller = new AbortController();
    let cancelled = false;
    const readRevision = localRevision.current;
    setConfirmation(null);

    const localWidgets = loadWidgetsFromLocalStorage({
      storage: window.localStorage,
      machineId,
      storageKey,
      legacyStorageKey,
      legacyUnversionedStorageKey,
      validSourcesKey,
    });
    setSavedWidgets(localWidgets);

    getSharedDashboardLayout(
      workspaceId,
      machineId,
      configVersion,
      controller.signal,
    )
      .then((layout) => {
        // Uma edicao posterior ao GET vence tambem no estado/cache e nas
        // migracoes. Conferir so o marcador permitia reaplicar e gravar o antigo.
        if (cancelled || readRevision !== localRevision.current) return;
        const confirmed = confirmLayoutResponse(layout, workspaceId, machineId, configVersion);
        if (layout.exists) {
          const remoteWidgets = normalizeDashboardWidgets(layout.widgets, machineId);
          // jsonb pode devolver chaves em outra ordem, sem mudar o layout.
          const normalizationChanged = !jsonStructurallyEqual(remoteWidgets, layout.widgets);
          saveLocalDashboardWidgets(window.localStorage, storageKey, remoteWidgets);
          setSavedWidgets(remoteWidgets);
          // Normalizacao/migracao dispara escrita: so uma proxima leitura pode
          // confirmar a revisao persistida, sem usar optimistic UI como prova.
          if (!normalizationChanged) {
            setConfirmation({ scope: confirmationScope, value: confirmed });
          }
          if (normalizationChanged) {
            void saveSharedDashboardWidgets(
              workspaceId,
              machineId,
              configVersion,
              remoteWidgets,
            ).catch((error) => {
              console.warn("Falha ao salvar layout compartilhado normalizado.", error);
            });
          }
          return;
        }

        if (localWidgets.length > 0) {
          void saveSharedDashboardWidgets(
            workspaceId,
            machineId,
            configVersion,
            localWidgets,
          ).catch((error) => {
            console.warn("Falha ao migrar widgets locais para layout compartilhado.", error);
          });
        } else {
          setConfirmation({ scope: confirmationScope, value: confirmed });
        }
      })
      .catch((error) => {
        if (isAbortError(error)) return;
        console.warn("Falha ao carregar layout compartilhado; usando cache local.", error);
      });

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [
    enabled,
    configVersion,
    legacyStorageKey,
    legacyUnversionedStorageKey,
    machineId,
    storageKey,
    validSourcesKey,
    workspaceId,
    confirmationScope,
  ]);

  const persist = useCallback(
    (update: DashboardWidget[] | ((current: DashboardWidget[]) => DashboardWidget[])) => {
      setConfirmation(null);
      localRevision.current++;
      setSavedWidgets((current) => {
        const rawNext = typeof update === "function" ? update(current) : update;
        const next = normalizeDashboardWidgets(rawNext, machineId);
        if (typeof window !== "undefined") {
          saveLocalDashboardWidgets(window.localStorage, storageKey, next);
        }
        void saveSharedDashboardWidgets(workspaceId, machineId, configVersion, next).catch(
          (error) => {
            console.warn("Falha ao salvar widgets no layout compartilhado.", error);
          },
        );
        return next;
      });
    },
    [configVersion, machineId, storageKey, workspaceId],
  );

  const addWidget = useCallback(
    (widget: DashboardWidget, seedWidgets: DashboardWidget[] = []) => {
      persist((current) => [...(current.length > 0 ? current : seedWidgets), widget]);
    },
    [persist],
  );

  const removeWidget = useCallback(
    (id: string) => {
      persist((current) => current.filter((widget) => widget.id !== id));
    },
    [persist],
  );

  const upsertWidget = useCallback(
    (widget: DashboardWidget, seedWidgets: DashboardWidget[] = []) => {
      persist((current) => {
        const base = current.length > 0 ? current : seedWidgets;
        const exists = base.some((candidate) => candidate.id === widget.id);
        return exists
          ? base.map((candidate) => (candidate.id === widget.id ? widget : candidate))
          : [...base, widget];
      });
    },
    [persist],
  );

  /**
   * Grava um LOTE de cards numa transacao so.
   *
   * Existe porque chamar `upsertWidget` em laco disparava um PUT por card:
   * requisicoes concorrentes, sem ordem garantida no servidor, e a ultima a
   * chegar vencia — podia ser um estado parcial do arrasto. Com o lote, o
   * layout viaja inteiro e uma vez.
   */
  const upsertWidgets = useCallback(
    (widgets: DashboardWidget[], seedWidgets: DashboardWidget[] = []) => {
      if (widgets.length === 0) return;
      persist((current) => {
        const base = current.length > 0 ? current : seedWidgets;
        const porId = new Map(base.map((widget) => [widget.id, widget]));
        for (const widget of widgets) porId.set(widget.id, widget);
        return [...porId.values()];
      });
    },
    [persist],
  );

  const reorderWidgets = useCallback(
    (widgets: DashboardWidget[]) => {
      persist(widgets.map((widget, order) => ({ ...widget, layout: { ...widget.layout, order } })));
    },
    [persist],
  );

  const reset = useCallback(() => {
    persist([]);
  }, [persist]);

  return {
    remoteConfirmation: enabled && confirmation?.scope === confirmationScope ? confirmation.value : null,
    savedWidgets,
    addWidget,
    removeWidget,
    upsertWidget,
    upsertWidgets,
    reorderWidgets,
    reset,
  };
}

function filterWidgetsForCurrentSignalStructure(
  widgets: DashboardWidget[],
  validSourceSet: Set<string> | null,
): DashboardWidget[] {
  if (!validSourceSet) return widgets;
  return widgets.filter((widget) => {
    if (!isPhysicalGatewaySource(widget.source)) return true;
    return validSourceSet.has(widget.source);
  });
}

function isPhysicalGatewaySource(source: string): boolean {
  return /^G\d+_P\d+_(?:s|v|acc)$/.test(source);
}

function loadWidgetsFromLocalStorage({
  storage,
  machineId,
  storageKey,
  legacyStorageKey,
  legacyUnversionedStorageKey,
  validSourcesKey,
}: {
  storage: Storage;
  machineId: string;
  storageKey: string;
  legacyStorageKey: string;
  legacyUnversionedStorageKey: string;
  validSourcesKey: string;
}): DashboardWidget[] {
  if (storage.getItem(storageKey) !== null) {
    return loadLocalDashboardWidgets(storage, storageKey, machineId);
  }

  let legacyWidgets = loadLocalDashboardWidgets(storage, legacyStorageKey, machineId);
  let loadedFromUnversionedLegacy = false;
  if (legacyWidgets.length === 0 && legacyUnversionedStorageKey !== legacyStorageKey) {
    legacyWidgets = loadLocalDashboardWidgets(
      storage,
      legacyUnversionedStorageKey,
      machineId,
    );
    loadedFromUnversionedLegacy = legacyWidgets.length > 0;
  }
  if (loadedFromUnversionedLegacy) {
    const validSourceSet = validSourcesKey ? new Set(validSourcesKey.split("|")) : null;
    legacyWidgets = filterWidgetsForCurrentSignalStructure(legacyWidgets, validSourceSet);
  }
  if (legacyWidgets.length > 0) {
    saveLocalDashboardWidgets(storage, storageKey, legacyWidgets);
  }
  return legacyWidgets;
}

function isAbortError(error: unknown): boolean {
  return error instanceof DOMException && error.name === "AbortError";
}
